import { Box, Container, Divider, Drawer, Grid, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useCallback, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { AxiosResponse } from "axios";
import { PropagateLoader } from "react-spinners";
import { boxStyle, flyInContent, flyInMainWrapper } from "../../../AppGlobalStyles";
import { fieldVariant, isCancelledError, success, toastError } from "../../../utils/AppUtils";
import StatusIconTextNotification, { MessageTypes } from "../StatusIconTextNotification/StatusIconTextNotification";
import { isValidLength } from "../../../validators/validators";
import FlyInDrawerFooter from "../FlyInDrawerFooter/FlyInDrawerFooter";
import FlyInDrawerHeader from "../FlyInDrawerHeader/FlyInDrawerHeader";
import { isHttpErrorMessageAvailable } from "../../../config/axios/Axios";
import { convertFromUTCToLocal, formatDateTo, getDurationStringFromNow } from "../../../utils/DateTimeUtils";
import AbstractInfiniteScroll from "../AbstractInfiniteScroll/AbstractInfiniteScroll";
import { addComment, getCommentsForJourney } from "../../../config/axios/apis/journey/journey";

interface IProps {
    buttonTitle?: any;
    closeDrawer: () => void;
    updateCommentCount?: () => void;
    journeyId: number;
    open: boolean;
}

interface Comment {
    id: number;
    content: string;
    updated: Date | string;
    byUser: { firstName: string; lastName: string; name: string };
}

interface FormData {
    commentContent: string;
}

const commentYupShape = {
    commentContent: yup
        .string()
        .required("Please input your comment.")
        .default("")
        .test("length", "Please input a comment between 5 and 5000 characters.", (val) => {
            if (val) {
                return !isValidLength(val, 5, 5000);
            }
            return true;
        }),
};

const schema = yup.object().shape({ ...commentYupShape });

const JourneyCommentsFlyIn = ({ closeDrawer, buttonTitle, updateCommentCount, journeyId, open }: IProps) => {
    const theme = useTheme();
    const { formatMessage } = useIntl();
    const [commentList, setCommentList] = useState<Comment[]>([]);

    const { handleSubmit, control, reset } = useForm<FormData>({
        resolver: yupResolver(schema),
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: { commentContent: "" },
        criteriaMode: "all",
    });

    const handleClose = () => {
        setCommentList([]);
        reset();
        closeDrawer();
    };

    const onSubmit = (data: FormData) => {
        return addComment(journeyId, { comments: [{ content: data.commentContent }] })
            .then(() => {
                success(<FormattedMessage id="order.comment.message.success" defaultMessage="Comment successfully saved." />);
                handleClose();
                if (updateCommentCount) {
                    updateCommentCount();
                }
            })
            .catch((error) => {
                if (isCancelledError(error)) {
                    return;
                }
                if (!isHttpErrorMessageAvailable(error)) {
                    toastError(error?.response?.data?.message ? error?.response?.data?.message : error?.message);
                }
            });
    };

    const getData = useCallback(
        (signal: AbortSignal, pageNumber: number) => {
            return getCommentsForJourney(
                {
                    filterBy: [
                        {
                            key: "journeyId",
                            operator: "equals",
                            value: journeyId,
                        },
                    ],
                    orderBy: [
                        {
                            key: "updated",
                            order: "DESC",
                        },
                    ],
                },
                {
                    signal,
                    params: {
                        page: pageNumber,
                        size: 20,
                        journeyUatStatus: "All",
                    },
                },
            );
        },
        [journeyId],
    );

    const onData = useCallback((res: AxiosResponse | null) => {
        if (res === null) {
            setCommentList([]);
            return;
        }

        const mappedData = res.data.data.map((item) => ({
            id: item.id,
            content: item.content,
            updated: item.updated,
            byUser: {
                firstName: item.ownerFirstName,
                lastName: item.ownerLastName,
                name: `${item.ownerFirstName} ${item.ownerLastName}`,
            },
        }));

        setCommentList((prev) => prev.concat(mappedData));
    }, []);

    return (
        <Drawer
            open={open}
            onClose={handleClose}
            anchor="right"
            slotProps={{
                paper: { sx: { width: 450, overflowX: "hidden", "@media(max-width:767px)": { width: "100%", overflowX: "unset" } } },
            }}
        >
            <Container sx={flyInMainWrapper}>
                <FlyInDrawerHeader close={handleClose} title={<FormattedMessage id="order.title.comment" defaultMessage="Make a comment" />} />
                <Grid container sx={flyInContent}>
                    <Grid size={12}>
                        <StatusIconTextNotification
                            textlineOne={`Journey found | 1h UID ${journeyId}`}
                            type={MessageTypes.SUCCESS}
                            style={{ marginBottom: "1.5rem", paddingLeft: "0.5rem", paddingRight: "0.5rem" }}
                        />
                        <Box sx={boxStyle}>
                            <Controller
                                control={control}
                                name="commentContent"
                                render={({ field, fieldState: { invalid, error } }) => (
                                    <TextField
                                        {...field}
                                        id="comment-content"
                                        variant={fieldVariant}
                                        margin="normal"
                                        color="primary"
                                        fullWidth
                                        multiline
                                        required
                                        rows={5}
                                        label={<FormattedMessage id="order.comment.label.add-a-comment" defaultMessage="Add a comment..." />}
                                        placeholder={formatMessage({ id: "order.comment.label.add-a-comment", defaultMessage: "Add a comment..." })}
                                        error={invalid}
                                        helperText={
                                            error ? (
                                                <FormattedMessage id={`order.comment.no-comment.error.${error.type}`} defaultMessage={error.message} />
                                            ) : (
                                                " "
                                            )
                                        }
                                    />
                                )}
                            />
                        </Box>
                        <AbstractInfiniteScroll
                            getData={getData}
                            onData={onData}
                            loader={<PropagateLoader cssOverride={{ height: "15px" }} color={theme.palette.primary.main} />}
                            noResults={
                                <div style={{ padding: "0.625rem 0.5rem" }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: "ink.soft",
                                        }}
                                    >
                                        <FormattedMessage id="order.comment.message.no-comments" defaultMessage="There are no comments for this journey." />
                                    </Typography>
                                </div>
                            }
                        >
                            {commentList.map((comment) => (
                                <div key={comment.id}>
                                    <div style={{ padding: "0.625rem 0.5rem" }}>
                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", paddingBottom: "5px" }}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 700,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {comment.byUser.name}
                                            </Typography>
                                            <Tooltip arrow placement="top" title={formatDateTo(convertFromUTCToLocal(comment.updated as string), "lll")}>
                                                <Typography variant="body2" sx={{ padding: "0 10px", whiteSpace: "nowrap" }}>
                                                    {getDurationStringFromNow(comment.updated as string)}
                                                </Typography>
                                            </Tooltip>
                                        </div>
                                        <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }}>
                                            {comment.content}
                                        </Typography>
                                    </div>
                                    <Divider />
                                </div>
                            ))}
                        </AbstractInfiniteScroll>
                    </Grid>
                </Grid>
                <FlyInDrawerFooter
                    onCloseBtn={handleClose}
                    onSubmitBtn={handleSubmit(onSubmit)}
                    submitBtnTxt={buttonTitle}
                    closeBtnTxt={<FormattedMessage id="common.button-label.cancel" defaultMessage="Cancel" />}
                />
            </Container>
        </Drawer>
    );
};

export default JourneyCommentsFlyIn;
